import { useId, useRef, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { Eyebrow } from './ui';
import { useModalBehaviour } from './use-modal';

/**
 * A modal whose BODY SCROLLS while its heading and its buttons stay put.
 *
 * The distinction is the whole point. `ConfirmDialog` holds a question short
 * enough to read at a glance, so it needs none of this. A panel with real
 * content in it — every permission a provider is about to be granted, with a
 * reason for each — is taller than a laptop viewport, and the two obvious ways
 * of handling that are both wrong: letting the panel grow puts the Continue
 * button below the fold, where somebody scrolls the page looking for it; and
 * scrolling the whole panel takes the heading away, so halfway down there is
 * nothing on screen saying what is being agreed to.
 *
 * So the panel is a fixed-height column: header, a scrolling middle, footer.
 * The buttons are always reachable and the heading is always visible, however
 * much is between them.
 *
 * Keyboard and focus behaviour come from `useModalBehaviour`, shared with
 * `ConfirmDialog` so there is one implementation of the part that is easy to
 * get wrong.
 */
export function Modal({
  open,
  title,
  eyebrow,
  subtitle,
  maxWidth = 460,
  busy = false,
  closeLabel = 'Close',
  onClose,
  footer,
  children,
}: {
  open: boolean;
  title: string;
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  /** In pixels. The content decides this, not the component. */
  maxWidth?: number;
  /** While true, the backdrop and the close button stop dismissing it. */
  busy?: boolean;
  closeLabel?: string;
  onClose: () => void;
  /** Pinned below the scrolling body. The primary action belongs here. */
  footer?: ReactNode;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const headingId = useId();

  useModalBehaviour({ open, panel, onClose: () => !busy && onClose() });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-[20px]"
      // Clicking away closes — but only on the backdrop itself, so a drag that
      // ends outside the panel does not count as a decision. Text in a long
      // body gets selected by dragging far more often than in a short one.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div className="absolute inset-0 bg-ink/45 backdrop-blur-[2px]" aria-hidden="true" />

      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        style={{ maxWidth: `${maxWidth}px` }}
        className={clsx(
          'relative flex w-full flex-col overflow-hidden rounded-feature bg-card shadow-modal',
          // Never taller than the viewport, so the footer cannot be pushed off
          // the bottom of the screen. The gutter matches the wrapper's padding.
          'max-h-[calc(100vh-40px)]',
          'animate-rise',
        )}
      >
        {/* shrink-0 on the header and footer, min-h-0 on the body: without both,
            flexbox gives the body its full content height and the panel grows
            past the viewport instead of the body scrolling. */}
        <div className="flex shrink-0 items-start gap-[14px] border-b border-divider px-[28px] py-[22px]">
          <div className="min-w-0">
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            <h2
              id={headingId}
              className={clsx(
                'm-0 text-heading font-semibold text-ink',
                eyebrow && 'mt-[6px]',
              )}
            >
              {title}
            </h2>
            {subtitle && <p className="m-0 mt-[8px] text-body text-muted">{subtitle}</p>}
          </div>

          {/* Last in the DOM so it is not what takes focus when the panel
              opens, and `ml-auto` rather than a reordered layout so that a
              screen reader meets the heading before the way out of it. */}
          <button
            type="button"
            onClick={() => !busy && onClose()}
            disabled={busy}
            aria-label={closeLabel}
            className={clsx(
              'ml-auto shrink-0 rounded-[--radius-pill] border-0 bg-transparent',
              'px-[10px] py-[4px] text-subheading leading-none text-muted',
              'hover:bg-page disabled:opacity-40',
            )}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-[28px] py-[24px]">{children}</div>

        {footer && (
          <div className="shrink-0 border-t border-divider px-[28px] py-[20px]">{footer}</div>
        )}
      </div>
    </div>
  );
}
